import { clamp, damp, mat4LookAt, mat4Multiply, mat4Orthographic, mat4Perspective, type Mat4, type Vec3 } from "./math.js";

const degrees = (value: number): number => (value * Math.PI) / 180;

export const CAMERA_CONFIG = Object.freeze({
  yaw: degrees(45),
  pitch: degrees(48),
  zoom: 24,
  minimumZoom: 18,
  touchMinimumZoom: 12,
  maximumZoom: 64,
  distance: 112,
  targetOffset: Object.freeze({ x: -0.8, y: 0.15, z: -0.8 }),
  followSmoothing: 6,
  near: 0.1,
  far: 240,
});

export type CameraMode = "legacy" | "tps";
export type CameraCollisionProbe = (x: number, y: number, z: number, radius: number) => boolean;
export type CameraGroundBasis = Readonly<{ forwardX: number; forwardZ: number; rightX: number; rightZ: number }>;

/** The view matrix's X row is screen-right; its negative Z row looks into the scene. */
export function cameraGroundBasis(view: Mat4): CameraGroundBasis {
  const forwardLength = Math.hypot(view[2]!, view[10]!) || 1;
  const rightLength = Math.hypot(view[0]!, view[8]!) || 1;
  return {
    forwardX: -view[2]! / forwardLength,
    forwardZ: -view[10]! / forwardLength,
    rightX: view[0]! / rightLength,
    rightZ: view[8]! / rightLength,
  };
}

export const TPS_CAMERA_CONFIG = Object.freeze({
  distance: 7.5,
  minimumZoom: 3,
  maximumZoom: 13,
  targetHeight: 1.2,
  pitch: degrees(12),
  minimumPitch: degrees(-70),
  maximumPitch: degrees(65),
  fieldOfView: degrees(58),
  positionFollowResponse: 9,
  verticalFollowResponse: 4,
  movementDeadZone: 0.12,
  lookSensitivityMouse: 0.003,
  lookSensitivityVertical: 0.003,
  lookSensitivityTouch: 0.005,
  zoomSmoothing: 10,
  collisionReleaseResponse: 4,
  collisionRadius: 0.25,
  collisionStep: 0.35,
  shoulderOffset: 0.55,
  collisionRefinements: 6,
  closeFramingDistance: 3.4,
  closeFollowMaximumLag: 0.12,
  closeVerticalMaximumLag: 0.08,
  teleportDistance: 20,
  near: 0.1,
  far: 240,
});

export class Camera {
  collisionProbe: CameraCollisionProbe | undefined;
  readonly position: Vec3 = [0, 80, 80];
  readonly target: Vec3 = [0, 0, 12];
  view: Mat4 = mat4LookAt(this.position, this.target);
  projection: Mat4 = mat4Orthographic(-27, 27, -15, 15, CAMERA_CONFIG.near, CAMERA_CONFIG.far);
  viewProjection: Mat4 = mat4Multiply(this.projection, this.view);
  private aspect = 16 / 9;
  private initialized = false;
  private currentMode: CameraMode;
  private legacyZoom: number = CAMERA_CONFIG.zoom;
  private tpsZoom: number = TPS_CAMERA_CONFIG.distance;
  private tpsYaw = 0;
  private tpsPitch: number = TPS_CAMERA_CONFIG.pitch;
  private aimInitialized = false;
  private lookRevision = 0;
  private requestedDistance: number = TPS_CAMERA_CONFIG.distance;
  private collisionDistance: number = TPS_CAMERA_CONFIG.distance;
  private shoulderDistance: number = TPS_CAMERA_CONFIG.shoulderOffset;
  private readonly frustumPlanes = new Float32Array(24);

  constructor(mode: CameraMode = "legacy") {
    this.currentMode = mode;
  }

  get mode(): CameraMode { return this.currentMode; }
  get yaw(): number { return this.mode === "tps" ? this.tpsYaw : CAMERA_CONFIG.yaw; }
  get orbitYaw(): number { return this.mode === "tps" ? this.tpsYaw : CAMERA_CONFIG.yaw; }
  get pitch(): number { return this.mode === "tps" ? this.tpsPitch : CAMERA_CONFIG.pitch; }
  get aimYaw(): number { return wrapAngle(this.tpsYaw + Math.PI); }
  get aimPitch(): number { return -this.tpsPitch; }
  get aimDirection(): Vec3 {
    const horizontal = Math.cos(this.aimPitch);
    return [Math.sin(this.aimYaw) * horizontal, Math.sin(this.aimPitch), Math.cos(this.aimYaw) * horizontal];
  }
  get groundBasis(): CameraGroundBasis { return cameraGroundBasis(this.view); }

  setAim(yaw: number, pitch: number): void {
    if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return;
    this.tpsYaw = wrapAngle(yaw + Math.PI);
    this.tpsPitch = clamp(-pitch, TPS_CAMERA_CONFIG.minimumPitch, TPS_CAMERA_CONFIG.maximumPitch);
    this.aimInitialized = true;
    if (this.mode === "tps" && this.initialized) this.rebuildThirdPersonView(this.aspect);
  }

  rotateAim(yaw: number, pitch: number): void {
    if (this.mode !== "tps" || !Number.isFinite(yaw) || !Number.isFinite(pitch)) return;
    this.setAim(this.aimYaw + yaw, this.aimPitch + pitch);
    if (yaw !== 0) this.lookRevision++;
  }
  get manualLookRevision(): number { return this.lookRevision; }
  get distance(): number { return this.mode === "tps" ? this.collisionDistance : CAMERA_CONFIG.distance; }
  get zoom(): number { return this.mode === "tps" ? this.tpsZoom : this.legacyZoom; }

  set zoom(value: number) {
    if (!Number.isFinite(value)) return;
    if (this.mode === "tps") this.tpsZoom = clamp(value, TPS_CAMERA_CONFIG.minimumZoom, TPS_CAMERA_CONFIG.maximumZoom);
    else this.legacyZoom = value;
  }

  setMode(mode: CameraMode): void {
    if (mode === this.currentMode) return;
    this.currentMode = mode;
    this.initialized = false;
  }

  resetFollow(): void {
    this.initialized = false;
  }

  orbit(deltaX: number, deltaY: number, source: "mouse" | "touch"): void {
    if (this.mode !== "tps" || !this.initialized || !Number.isFinite(deltaX) || !Number.isFinite(deltaY)
      || (deltaX === 0 && deltaY === 0)) return;
    const horizontal = source === "touch" ? TPS_CAMERA_CONFIG.lookSensitivityTouch : TPS_CAMERA_CONFIG.lookSensitivityMouse;
    const vertical = source === "touch" ? TPS_CAMERA_CONFIG.lookSensitivityTouch : TPS_CAMERA_CONFIG.lookSensitivityVertical;
    this.rotateAim(-deltaX * horizontal, -deltaY * vertical);
  }

  adjustZoom(multiplier: number, minimumZoom: number = CAMERA_CONFIG.minimumZoom): void {
    if (!Number.isFinite(multiplier) || multiplier <= 0) return;
    if (Math.abs(multiplier - 1) < 0.000_001) return;
    if (this.mode === "tps") {
      this.zoom *= multiplier;
      return;
    }
    this.zoom = clamp(
      this.zoom * multiplier,
      clamp(minimumZoom, CAMERA_CONFIG.touchMinimumZoom, CAMERA_CONFIG.maximumZoom),
      CAMERA_CONFIG.maximumZoom,
    );
  }

  update(targetX: number, targetY: number, targetZ: number, aspect: number, delta: number, facingYaw?: number, movementHeading?: number, followElapsed = delta): void {
    const followDelta = Number.isFinite(followElapsed) ? Math.max(0, followElapsed) : 0;
    if (this.mode === "tps") {
      this.updateThirdPerson(targetX, targetY, targetZ, aspect, delta, facingYaw, movementHeading, followDelta);
      return;
    }
    const desiredX = targetX + CAMERA_CONFIG.targetOffset.x;
    const desiredY = targetY + CAMERA_CONFIG.targetOffset.y;
    const desiredZ = targetZ + CAMERA_CONFIG.targetOffset.z;
    if (!this.initialized) {
      this.target[0] = desiredX;
      this.target[1] = desiredY;
      this.target[2] = desiredZ;
      this.initialized = true;
    } else {
      this.target[0] = damp(this.target[0], desiredX, CAMERA_CONFIG.followSmoothing, followDelta);
      this.target[1] = damp(this.target[1], desiredY, CAMERA_CONFIG.followSmoothing, followDelta);
      this.target[2] = damp(this.target[2], desiredZ, CAMERA_CONFIG.followSmoothing, followDelta);
    }

    this.rebuild(aspect);
  }

  updateFree(targetX: number, targetY: number, targetZ: number, aspect: number): void {
    if (this.mode === "tps") {
      this.initialized = false;
      this.updateThirdPerson(targetX, targetY - TPS_CAMERA_CONFIG.targetHeight, targetZ, aspect, 0);
      return;
    }
    this.target[0] = targetX;
    this.target[1] = targetY;
    this.target[2] = targetZ;
    this.initialized = true;
    this.rebuild(aspect);
  }

  reset(): void {
    this.legacyZoom = CAMERA_CONFIG.zoom;
    this.tpsZoom = TPS_CAMERA_CONFIG.distance;
    this.tpsPitch = TPS_CAMERA_CONFIG.pitch;
    this.aimInitialized = false;
    this.initialized = false;
  }

  private updateThirdPerson(
    targetX: number, targetY: number, targetZ: number, aspect: number, delta: number, facingYaw?: number, movementHeading?: number,
    followElapsed = delta,
  ): void {
    const elapsed = Number.isFinite(delta) ? clamp(delta, 0, 0.1) : 0;
    const desiredY = targetY + TPS_CAMERA_CONFIG.targetHeight;
    // Character heading is +Z at yaw 0; the camera boom extends behind it.
    const initialYaw = facingYaw !== undefined && Number.isFinite(facingYaw) ? facingYaw + Math.PI : this.tpsYaw;
    if (!this.initialized || Math.hypot(targetX - this.target[0], desiredY - this.target[1], targetZ - this.target[2]) > TPS_CAMERA_CONFIG.teleportDistance) {
      this.target[0] = targetX;
      this.target[1] = desiredY;
      this.target[2] = targetZ;
      if (!this.aimInitialized) { this.tpsYaw = wrapAngle(initialYaw); this.aimInitialized = true; }
      this.requestedDistance = this.tpsZoom;
      this.collisionDistance = this.tpsZoom;
      this.initialized = true;
    } else {
      // Position follow uses wall time; current-frame input ownership and zoom
      // retain their existing simulation step below.
      this.target[0] = damp(this.target[0], targetX, TPS_CAMERA_CONFIG.positionFollowResponse, followElapsed);
      this.target[1] = damp(this.target[1], desiredY, TPS_CAMERA_CONFIG.verticalFollowResponse, followElapsed);
      this.target[2] = damp(this.target[2], targetZ, TPS_CAMERA_CONFIG.positionFollowResponse, followElapsed);
      this.requestedDistance = damp(this.requestedDistance, this.tpsZoom, TPS_CAMERA_CONFIG.zoomSmoothing, elapsed);
    }
    const probe = this.collisionProbe;
    if (probe?.(...this.target, TPS_CAMERA_CONFIG.collisionRadius)) {
      // Follow lag must not drag the boom's origin into a wall behind a player
      // who has already moved away from it.
      this.target[0] = targetX;
      this.target[1] = desiredY;
      this.target[2] = targetZ;
    }
    this.shoulderDistance = this.sweepShoulder();
    let primaryDistance = this.sweepBoom(this.tpsYaw);
    if (primaryDistance < TPS_CAMERA_CONFIG.closeFramingDistance) {
      // A short boom magnifies normal follow lag: running toward the eye can
      // otherwise crop the feet before side avoidance is needed. Limit only
      // that close-obstacle lag, and resweep from the corrected origin.
      const lagX = this.target[0] - targetX;
      const lagZ = this.target[2] - targetZ;
      const horizontalLag = Math.hypot(lagX, lagZ);
      const verticalLag = this.target[1] - desiredY;
      if (horizontalLag > TPS_CAMERA_CONFIG.closeFollowMaximumLag || Math.abs(verticalLag) > TPS_CAMERA_CONFIG.closeVerticalMaximumLag) {
        const scale = Math.min(1, TPS_CAMERA_CONFIG.closeFollowMaximumLag / (horizontalLag || 1));
        this.target[0] = targetX + lagX * scale;
        this.target[2] = targetZ + lagZ * scale;
        this.target[1] = desiredY + clamp(verticalLag, -TPS_CAMERA_CONFIG.closeVerticalMaximumLag, TPS_CAMERA_CONFIG.closeVerticalMaximumLag);
        this.shoulderDistance = this.sweepShoulder();
        primaryDistance = this.sweepBoom(this.tpsYaw);
      }
    }
    const safeDistance = primaryDistance;
    // Retract as soon as blocked; only the return to full distance is damped,
    // so smoothing never deliberately leaves the eye inside an obstruction.
    this.collisionDistance = safeDistance < this.collisionDistance
      ? safeDistance
      : damp(this.collisionDistance, safeDistance, TPS_CAMERA_CONFIG.collisionReleaseResponse, elapsed);
    this.rebuildThirdPersonView(aspect);
  }

  private rebuildThirdPersonView(aspect: number): void {
    const horizontal = Math.cos(this.pitch);
    const directionX = Math.sin(this.yaw) * horizontal;
    const directionY = Math.sin(this.pitch);
    const directionZ = Math.cos(this.yaw) * horizontal;
    this.position[0] = this.target[0] + Math.cos(this.yaw) * this.shoulderDistance + directionX * this.collisionDistance;
    this.position[1] = this.target[1] + directionY * this.collisionDistance;
    this.position[2] = this.target[2] - Math.sin(this.yaw) * this.shoulderDistance + directionZ * this.collisionDistance;
    if (Number.isFinite(aspect) && aspect > 0) this.aspect = aspect;
    // Preserve the short-axis field of view when rotating a phone. Portrait
    // gains vertical coverage without squeezing the player into a narrow slit.
    const fieldOfView = 2 * Math.atan(Math.tan(TPS_CAMERA_CONFIG.fieldOfView / 2) / Math.min(1, this.aspect));
    this.view = mat4LookAt(this.position, [this.position[0] - directionX, this.position[1] - directionY, this.position[2] - directionZ]);
    this.projection = mat4Perspective(fieldOfView, this.aspect, TPS_CAMERA_CONFIG.near, TPS_CAMERA_CONFIG.far);
    this.viewProjection = mat4Multiply(this.projection, this.view);
    const matrix = this.viewProjection;
    let plane = 0;
    for (let axis = 0; axis < 3; axis += 1) {
      for (let sign = -1; sign <= 1; sign += 2) {
        const a = matrix[3]! + sign * matrix[axis]!;
        const b = matrix[7]! + sign * matrix[4 + axis]!;
        const c = matrix[11]! + sign * matrix[8 + axis]!;
        const length = Math.hypot(a, b, c);
        this.frustumPlanes[plane++] = a / length;
        this.frustumPlanes[plane++] = b / length;
        this.frustumPlanes[plane++] = c / length;
        this.frustumPlanes[plane++] = (matrix[15]! + sign * matrix[12 + axis]!) / length;
      }
    }
  }

  private sweepShoulder(): number {
    if (!this.collisionProbe) return TPS_CAMERA_CONFIG.shoulderOffset;
    const blocked = (distance: number) => this.collisionProbe!(this.target[0] + Math.cos(this.yaw) * distance,
      this.target[1], this.target[2] - Math.sin(this.yaw) * distance, TPS_CAMERA_CONFIG.collisionRadius);
    const steps = Math.ceil(TPS_CAMERA_CONFIG.shoulderOffset / TPS_CAMERA_CONFIG.collisionStep);
    let safe = 0;
    for (let i = 1; i <= steps; i++) {
      let hit = i * TPS_CAMERA_CONFIG.shoulderOffset / steps;
      if (!blocked(hit)) { safe = hit; continue; }
      for (let j = 0; j < TPS_CAMERA_CONFIG.collisionRefinements; j++) {
        const mid = (safe + hit) / 2;
        if (blocked(mid)) hit = mid; else safe = mid;
      }
      return Math.max(0, safe - .005);
    }
    return safe;
  }

  private sweepBoom(yaw: number): number {
    const probe = this.collisionProbe;
    if (!probe) return this.requestedDistance;
    const directionX = Math.sin(yaw) * Math.cos(this.pitch);
    const directionY = Math.sin(this.pitch);
    const directionZ = Math.cos(yaw) * Math.cos(this.pitch);
    const blocked = (distance: number): boolean => probe(
      this.target[0] + Math.cos(yaw) * this.shoulderDistance + directionX * distance, this.target[1] + directionY * distance,
      this.target[2] - Math.sin(yaw) * this.shoulderDistance + directionZ * distance, TPS_CAMERA_CONFIG.collisionRadius,
    );
    const steps = Math.ceil(this.requestedDistance / TPS_CAMERA_CONFIG.collisionStep);
    const step = this.requestedDistance / steps;
    for (let index = 1; index <= steps; index++) {
      if (!blocked(index * step)) continue;
      let safe = (index - 1) * step;
      let hit = index * step;
      for (let refinement = 0; refinement < TPS_CAMERA_CONFIG.collisionRefinements; refinement++) {
        const middle = (safe + hit) / 2;
        if (blocked(middle)) hit = middle; else safe = middle;
      }
      return Math.max(0, safe - 0.005);
    }
    return this.requestedDistance;
  }

  private rebuild(aspect: number): void {
    const horizontal = Math.cos(CAMERA_CONFIG.pitch) * CAMERA_CONFIG.distance;
    this.position[0] = this.target[0] + Math.sin(CAMERA_CONFIG.yaw) * horizontal;
    this.position[1] = this.target[1] + Math.sin(CAMERA_CONFIG.pitch) * CAMERA_CONFIG.distance;
    this.position[2] = this.target[2] + Math.cos(CAMERA_CONFIG.yaw) * horizontal;
    if (Number.isFinite(aspect) && aspect > 0) this.aspect = aspect;
    // Preserve the requested world span on the shorter axis. Landscape keeps
    // its existing framing; portrait gains vertical coverage instead of a slit.
    const halfHeight = this.zoom * 0.5 / Math.min(1, this.aspect);
    const halfWidth = halfHeight * this.aspect;
    this.view = mat4LookAt(this.position, this.target);
    this.projection = mat4Orthographic(-halfWidth, halfWidth, -halfHeight, halfHeight, CAMERA_CONFIG.near, CAMERA_CONFIG.far);
    this.viewProjection = mat4Multiply(this.projection, this.view);
  }

  isSphereVisible(x: number, y: number, z: number, radius: number): boolean {
    const matrix = this.viewProjection;
    if (this.mode === "tps") {
      // Signed frustum-plane distance handles perspective depth and spheres
      // crossing the near plane; NDC radius padding is only valid for ortho.
      const planes = this.frustumPlanes;
      for (let index = 0; index < planes.length; index += 4) {
        if (planes[index]! * x + planes[index + 1]! * y + planes[index + 2]! * z + planes[index + 3]! < -radius) return false;
      }
      return true;
    }
    const clipX = matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!;
    const clipY = matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!;
    const clipZ = matrix[2]! * x + matrix[6]! * y + matrix[10]! * z + matrix[14]!;
    const clipW = matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!;
    const inverseW = 1 / (clipW || 1);
    const normalizedX = clipX * inverseW;
    const normalizedY = clipY * inverseW;
    const normalizedZ = clipZ * inverseW;
    const padX = radius * Math.abs(this.projection[0]!);
    const padY = radius * Math.abs(this.projection[5]!);
    return (
      normalizedX >= -1 - padX &&
      normalizedX <= 1 + padX &&
      normalizedY >= -1 - padY &&
      normalizedY <= 1 + padY &&
      normalizedZ >= -1.1 &&
      normalizedZ <= 1.1
    );
  }
}

function wrapAngle(value: number): number {
  return Math.atan2(Math.sin(value), Math.cos(value));
}
